import { useState } from "react";
import { useForm } from "react-hook-form";
import EmojiPicker from 'emoji-picker-react';
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type BoardFormValues = {
  name: string;
  emoji: string;
};

type CreateBoardFormProps = {
  companyId: string;
  onSuccess: () => void;
};

export const CreateBoardForm = ({ companyId, onSuccess }: CreateBoardFormProps) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const { toast } = useToast();
  const form = useForm<BoardFormValues>({
    defaultValues: {
      name: "",
      emoji: "💡",
    },
  });

  const onSubmit = async (data: BoardFormValues) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in to create a board",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from("feedback_boards")
        .insert({
          company_id: companyId,
          name: data.name,
          emoji: data.emoji,
          created_by: session.user.id
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Board created successfully",
      });
      
      form.reset();
      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create board",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Board Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Bug Reports" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="emoji"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Emoji</FormLabel>
              <FormControl>
                <Popover 
                  open={showEmojiPicker} 
                  onOpenChange={setShowEmojiPicker}
                  modal={true}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <span className="text-2xl mr-2">{field.value}</span>
                      <span className="text-muted-foreground">
                        Click to select emoji
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" side="right" align="start">
                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        field.onChange(emojiData.emoji);
                        setShowEmojiPicker(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full">Create Board</Button>
      </form>
    </Form>
  );
};